import * as THREE from 'three'
import type { EventBus } from '@/runtime/EventBus'
import type { PointerLockService, ViewRegistry } from '@/shared/types/services'
import type { ViewId } from '@/shared/types/view'

type PointerLockTarget = HTMLElement & {
  requestPointerLock?: () => Promise<void> | void
}

function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'then' in value &&
    typeof value.then === 'function'
  )
}

export class PointerLockServiceImpl implements PointerLockService {
  private viewRegistry: ViewRegistry | null = null
  private lockedViewIdValue: ViewId | null = null
  private pendingViewIdValue: ViewId | null = null
  private delta = new THREE.Vector2()
  private disposed = false

  constructor(
    private readonly target: HTMLElement,
    private readonly events: EventBus
  ) {
    window.addEventListener('mousemove', this.handleMouseMove)
    window.addEventListener('blur', this.handleBlur)
    document.addEventListener('pointerlockchange', this.handlePointerLockChange)
    this.events.on('view.removed', this.handleViewRemoved)
  }

  get isLocked(): boolean {
    return document.pointerLockElement === this.target
  }

  get lockedViewId(): ViewId | null {
    return this.lockedViewIdValue
  }

  get pendingViewId(): ViewId | null {
    return this.pendingViewIdValue
  }

  bindViewRegistry(viewRegistry: ViewRegistry): void {
    this.viewRegistry = viewRegistry
    if (this.lockedViewIdValue && !this.viewRegistry.get(this.lockedViewIdValue)) {
      this.clearSession()
    }
    if (this.pendingViewIdValue && !this.viewRegistry.get(this.pendingViewIdValue)) {
      this.pendingViewIdValue = null
    }
  }

  request(viewId: ViewId): boolean {
    if (this.disposed) {
      return false
    }

    if (this.viewRegistry && !this.viewRegistry.get(viewId)) {
      return false
    }

    if (this.isLocked) {
      this.lockedViewIdValue = viewId
      this.pendingViewIdValue = null
      this.delta.set(0, 0)
      return true
    }

    const pointerLockTarget = this.target as PointerLockTarget
    if (typeof pointerLockTarget.requestPointerLock !== 'function') {
      return false
    }

    this.pendingViewIdValue = viewId
    this.delta.set(0, 0)

    const result = pointerLockTarget.requestPointerLock()
    if (isPromiseLike(result)) {
      void result.catch(() => {
        if (!this.isLocked && this.pendingViewIdValue === viewId) {
          this.pendingViewIdValue = null
        }
      })
    }

    return true
  }

  isLockedFor(viewId: ViewId): boolean {
    return this.isLocked && this.lockedViewIdValue === viewId
  }

  peekDelta(viewId?: ViewId | null): THREE.Vector2 {
    if (!this.isLocked) {
      return new THREE.Vector2()
    }
    if (viewId && this.lockedViewIdValue !== viewId) {
      return new THREE.Vector2()
    }
    return this.delta.clone()
  }

  consumeDelta(viewId?: ViewId | null): THREE.Vector2 {
    const movement = this.peekDelta(viewId)
    if (!viewId || this.lockedViewIdValue === viewId) {
      this.delta.set(0, 0)
    }
    return movement
  }

  exit(): void {
    if (this.disposed) {
      return
    }

    this.pendingViewIdValue = null
    this.delta.set(0, 0)

    if (document.pointerLockElement === this.target) {
      document.exitPointerLock?.()
      if (document.pointerLockElement === this.target) {
        this.clearSession()
      }
      return
    }

    this.clearSession()
  }

  dispose(): void {
    if (this.disposed) {
      return
    }

    this.exit()
    this.disposed = true
    window.removeEventListener('mousemove', this.handleMouseMove)
    window.removeEventListener('blur', this.handleBlur)
    document.removeEventListener('pointerlockchange', this.handlePointerLockChange)
    this.events.off('view.removed', this.handleViewRemoved)
    this.viewRegistry = null
    this.clearSession()
  }

  private clearSession(): void {
    this.lockedViewIdValue = null
    this.pendingViewIdValue = null
    this.delta.set(0, 0)
  }

  private resolveLockedViewId(): ViewId | null {
    if (this.pendingViewIdValue) {
      if (!this.viewRegistry || this.viewRegistry.get(this.pendingViewIdValue)) {
        return this.pendingViewIdValue
      }
    }

    if (this.lockedViewIdValue) {
      if (!this.viewRegistry || this.viewRegistry.get(this.lockedViewIdValue)) {
        return this.lockedViewIdValue
      }
    }

    return this.viewRegistry?.getActive()?.id ?? null
  }

  private readonly handleMouseMove = (event: MouseEvent) => {
    if (!this.isLocked) {
      return
    }

    this.delta.x += event.movementX ?? 0
    this.delta.y += event.movementY ?? 0
  }

  private readonly handleBlur = () => {
    if (this.isLocked || this.pendingViewIdValue) {
      this.exit()
      return
    }

    this.delta.set(0, 0)
  }

  private readonly handlePointerLockChange = () => {
    if (document.pointerLockElement === this.target) {
      this.lockedViewIdValue = this.resolveLockedViewId()
      this.pendingViewIdValue = null
      this.delta.set(0, 0)
      return
    }

    this.clearSession()
  }

  private readonly handleViewRemoved = ({ viewId }: { viewId: ViewId }) => {
    if (viewId !== this.lockedViewIdValue && viewId !== this.pendingViewIdValue) {
      return
    }

    this.exit()
  }
}
